import React from "react";
import { graphql } from "gatsby";
import Layout from "../components/layout";
import SEO from "../components/seo";
import BlogList from "../components/BlogList";
import { HeaderContent } from "../components/page";
import ContactFooter from "../components/ContactFooter";
import FooterContent from "../components/page/FooterContent";
import { getBlogPagePath } from "../utils/blogPaths";

import "../templates/page.scss";

const BlogArchiveTemplate = ({
  data: { blogPage, posts, allPosts, site },
  pageContext: {
    currentPage,
    totalPages,
    categorySlug,
    categoryName,
    previousPagePath,
    nextPagePath,
  },
}) => {
  const featuredImage = {
    fluid: blogPage?.featuredImage?.node?.localFile?.childImageSharp?.fluid,
    alt: blogPage?.featuredImage?.node?.alt || ``,
  };

  const src = featuredImage?.fluid?.src || "";
  const acf = blogPage?.acfPageInterne;
  const siteUrl = site.siteMetadata.siteUrl.replace(/\/$/, "");

  const isCategoryArchive = Boolean(categorySlug);
  const selectedCategory = categorySlug || "all";

  let seoTitle = blogPage?.seo?.title || blogPage?.title || "Blog";
  let seoDescription = blogPage?.seo?.metaDesc || "";

  if (isCategoryArchive) {
    seoTitle =
      currentPage > 1
        ? `${categoryName} – Page ${currentPage} | Blog`
        : `${categoryName} – Blog`;
    seoDescription = `Articles du blog dans la catégorie ${categoryName}.`;
  } else if (currentPage > 1) {
    seoTitle = `Blog – Page ${currentPage}`;
    seoDescription = `Page ${currentPage} du blog Normandie Nuisibles.`;
  }

  const canonicalPath = getBlogPagePath({
    categorySlug,
    page: currentPage,
  });

  return (
    <Layout post={blogPage}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        post={blogPage}
        seoOverrides={{
          title: seoTitle,
          metaDesc: seoDescription,
          canonical: `${siteUrl}${canonicalPath}`,
          prev: previousPagePath,
          next: nextPagePath,
          ogType: "website",
        }}
      />

      <div className={"page blog" + (categorySlug ? ` category-${categorySlug}` : "")}>
        <section className="section content">
          {src && <HeaderContent acf={acf} post={blogPage} src={src} />}
        </section>
        <BlogList
          posts={posts.nodes}
          allPosts={allPosts.nodes}
          currentPage={currentPage}
          totalPages={totalPages}
          selectedCategory={selectedCategory}
        />
        <FooterContent content={acf?.contentfooter} />
      </div>
      <ContactFooter />
    </Layout>
  );
};

export default BlogArchiveTemplate;

export const pageQuery = graphql`
  query BlogArchive($postIds: [String!]!) {
    blogPage: wpPage(slug: { eq: "blog" }) {
      id
      uri
      title
      slug
      pageCategories {
        nodes {
          name
        }
      }
      seo {
        canonical
        title
        metaDesc
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphTitle
        opengraphDescription
        opengraphUrl
        opengraphImage {
          localFile {
            childImageSharp {
              fluid(maxWidth: 700, quality: 90, toFormat: JPG) {
                src
              }
            }
          }
        }
        breadcrumbs {
          url
          text
        }
        schema {
          raw
        }
      }
      acfPageInterne {
        contenthead
        contentfooter {
          text
          image {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 412, quality: 90) {
                  src
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
      }
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              fluid(maxWidth: 1000, quality: 100) {
                ...GatsbyImageSharpFluid_tracedSVG
              }
            }
          }
        }
      }
    }

    posts: allWpPost(
      filter: { id: { in: $postIds } }
      sort: { fields: [date], order: DESC }
    ) {
      nodes {
        id
        title
        excerpt
        slug
        date
        categories {
          nodes {
            id
            name
            slug
          }
        }
        featuredImage {
          node {
            altText
            localFile {
              childImageSharp {
                fluid(maxWidth: 800, quality: 90) {
                  src
                  ...GatsbyImageSharpFluid_withWebp_noBase64
                }
              }
            }
          }
        }
      }
    }

    allPosts: allWpPost(sort: { fields: [date], order: DESC }) {
      nodes {
        id
        categories {
          nodes {
            id
            name
            slug
          }
        }
      }
    }

    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;
